import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { withTranslation } from "../translation/";
import {
  getFacets,
  getStateForPriceSlider,
  done,
  changeMultiFacetAndApplyIfLargeMedium,
  selectAll,
  clearAll,
  clear,
  clearSelections
} from "../../state/modules/search";
import { COLOUR_FACET_ID } from "../../state/modules/facet/constants";
import { getSwatches } from "../../state/modules/facet/selectors/getSwatches";
import { analyticsOpenRefinementType } from "../../state/modules/analytics/actions";
import { getFilterOverlayVisible } from "../../state/modules/layout/selectors";
import { hideFilterOverlay } from "../../state/modules/layout/actions";
import { getColourEnrichments } from "../../state/modules/facet/getColourEnrichments";
import { getNewFacets } from "../../state/modules/featureToggles/selectors";
import noTouchAction from "../../helpers/noTouchAction";
import styles from "./RefinementList.css";
import PrimaryList from "./PrimaryList";
import SecondaryList from "./SecondaryList";
import classNames from "classnames/bind";
import { getFacetWithSwatches } from "../../client/getFacetWithSwatches";
import {
  MOBILE_DONE,
  MOBILE_VIEW_ITEMS
} from "../../state/modules/search/constants";

const styleContext = classNames.bind(styles);

const initialLocalComponentState = {
  secondaryListIsOpen: false
};
export class RefinementList extends Component {
  state = initialLocalComponentState;

  resetStateToInitial() {
    this.setState(initialLocalComponentState);
  }

  componentDidMount() {
    const { isVisible, facets, getColourEnrichments } = this.props;
    noTouchAction(isVisible);

    if (facets.some(({ id }) => id === COLOUR_FACET_ID)) {
      getColourEnrichments();
    }
  }

  componentWillReceiveProps(nextProps) {
    const { clearSelections, isVisible: wasVisible } = this.props;
    const willBeVisible = nextProps.isVisible;
    noTouchAction(willBeVisible);
    if (willBeVisible !== wasVisible && wasVisible) {
      this.resetStateToInitial();
      clearSelections();
    }
  }

  getOpenItem() {
    const facet = this.props.facets.find(
      ({ id }) => id === this.state.openItemId
    );

    if (!facet || facet.id !== COLOUR_FACET_ID) {
      return facet;
    }

    return getFacetWithSwatches({ facet, swatchesMap: this.props.swatchesMap });
  }

  itemCount(facetId = false) {
    if (facetId) {
      const selectedFacet = this.props.facets.filter(
        f => f.id === facetId && f.hasSelectedValues
      );

      if (selectedFacet.length) {
        return selectedFacet[0].facetValues
          .map(fv => {
            if (fv.isSelected === true) {
              return fv.count;
            }

            return 0;
          })
          .reduce((accumulator, count) => accumulator + count);
      }
    }

    return this.props.itemCount;
  }

  open = ({ id, name }) => {
    this.setState({
      openItemId: id,
      secondaryListIsOpen: true
    });
    this.props.analyticsOpenRefinementType({ id, name });
  };

  back = facet => {
    this.props.done(facet, MOBILE_DONE);
    this.resetStateToInitial();
  };

  handleViewListClick = facet => {
    this.props.hideFilterOverlay();
    this.props.done(facet, MOBILE_VIEW_ITEMS);
    this.resetStateToInitial();
  };

  primaryList() {
    return (
      <PrimaryList
        facets={this.props.facets}
        newFacets={this.props.newFacets}
        range={this.props.range}
        clearAll={this.props.clearAll}
        formatTranslation={this.props.formatTranslation}
        formatCurrency={this.props.formatCurrency}
        itemCount={this.props.itemCount}
        open={this.open}
        viewList={this.handleViewListClick}
        styles={styles}
      />
    );
  }

  secondaryList() {
    const openItem = this.getOpenItem();
    const itemCount = openItem ? this.itemCount(openItem.id) : 0;

    return (
      <SecondaryList
        facet={openItem}
        itemCount={itemCount}
        styles={styles}
        clear={this.props.clear}
        selectAll={this.props.selectAll}
        changeMultiFacet={this.props.changeMultiFacet}
        viewList={this.handleViewListClick}
        back={this.back}
        secondaryListIsOpen={this.state.secondaryListIsOpen}
      />
    );
  }

  blockFocusScroll /* istanbul ignore next */ = node => {
    // if user changes focus when animating between primaryList/secondaryList
    // browser tries to scroll element in to view the element
    // really unlikely scenario to happen
    // remove this is we think is unecessary
    this.refinementsListEl = node;
    if (this.refinementsListEl) {
      this.refinementsListEl.addEventListener("focusin", this.onFocusIn);
    }
  };

  onFocusIn /* istanbul ignore next */ = () => {
    this.refinementsListEl.scrollLeft = 0;
  };

  /* istanbul ignore next */
  componentWillUnmount() {
    if (this.refinementsListEl) {
      this.refinementsListEl.removeEventListener("focusin", this.onFocusIn);
    }
  }

  render() {
    const {
      props: { isVisible },
      state: { secondaryListIsOpen }
    } = this;

    if (!isVisible) {
      return null;
    }

    return (
      <div
        data-auto-id="smallRefinements"
        className={styles.refinementList}
        ref={this.blockFocusScroll}
      >
        <div
          className={styleContext({
            navigation: true,
            navigation__secondary: secondaryListIsOpen
          })}
        >
          {this.primaryList()}
          {this.secondaryList()}
        </div>
      </div>
    );
  }
}

RefinementList.propTypes = {
  facets: PropTypes.arrayOf(PropTypes.object).isRequired,
  newFacets: PropTypes.array.isRequired,
  range: PropTypes.object.isRequired,
  clearAll: PropTypes.func.isRequired,
  clear: PropTypes.func.isRequired,
  selectAll: PropTypes.func.isRequired,
  hideFilterOverlay: PropTypes.func.isRequired,
  changeMultiFacet: PropTypes.func.isRequired,
  done: PropTypes.func.isRequired,
  clearSelections: PropTypes.func.isRequired,
  formatTranslation: PropTypes.func.isRequired,
  formatCurrency: PropTypes.func.isRequired,
  itemCount: PropTypes.number.isRequired,
  isVisible: PropTypes.bool.isRequired,
  analyticsOpenRefinementType: PropTypes.func.isRequired,
  swatchesMap: PropTypes.object.isRequired,
  getColourEnrichments: PropTypes.func.isRequired
};

RefinementList.displayName = "RefinementList";

/* istanbul ignore next */
function mapDispatchToProps(dispatch) {
  return {
    ...bindActionCreators(
      {
        hideFilterOverlay,
        changeMultiFacet: changeMultiFacetAndApplyIfLargeMedium,
        done,
        selectAll,
        clearAll,
        clear,
        clearSelections,
        analyticsOpenRefinementType
      },
      dispatch
    ),
    getColourEnrichments: () => dispatch(getColourEnrichments())
  };
}

/* istanbul ignore next */
function mapStateToProps(state) {
  return {
    range: getStateForPriceSlider(state),
    facets: getFacets(state),
    newFacets: getNewFacets(state),
    itemCount: state.search.itemCount,
    isVisible: getFilterOverlayVisible(state),
    swatchesMap: getSwatches(state)
  };
}

const RefinementListContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation(RefinementList));

export default RefinementListContainer;
